import React, { useEffect, useState, useMemo, useCallback, memo, useRef } from 'react';
import { debounce } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';

import SearchBar from './_components/SearchBar';

import { fetchSearchResults } from 'api/searchAPI';
import SearchResults from './_components/SearchResults';
import { addData } from 'store/secondaryVideoTabsDataSlice';
import { RootState } from 'store';
import { setSearchTerm as setSecondarySearchKey, getSecondaryOpenVideo } from 'store/secondaryViewSlice';
import { addSpeakers, getSpeaker } from 'store/spearkeSlice';

import api from 'services/api';

interface ISearchView {
  videoHashId: string;
  videoShots: any[];
  currentVideoShot: number;
  selected: number;
  channelId: string;
  aesKeyId: string;
  decryptionKeys: any;
  navigateVideoPage: any;
  isResizing: boolean;
}
function SearchView({
  videoHashId,
  videoShots,
  currentVideoShot,
  selected,
  channelId,
  aesKeyId,
  decryptionKeys,
  navigateVideoPage,
  isResizing
}: ISearchView) {
  const [searchTerm, setSearchTerm] = useState('');
  const options = ['Timeline'];
  // const options = ['Timeline', 'Notes', 'Chat', 'Transcript'];
  const [selectedOption, setSelectedOption] = useState(['Timeline']);
  const keyId = channelId + videoHashId + currentVideoShot;
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const secondaryViewVideos = useSelector((state: RootState) => state.secondaryView);

  const dispatch = useDispatch();

  const videoShotId = useMemo(() => {
    if (!videoShots?.length) return;
    const idx = videoShots[currentVideoShot]?.shot_hash_id;
    return idx;
  }, [keyId, videoHashId, channelId, videoShots, currentVideoShot]);

  const searchData = useSelector(
    (state: RootState) => state.secondaryViewTab.videos.find((video) => video.videoHashId === keyId)?.search
  );

  const debouncedSearch = useCallback(
    debounce(async () => {
      if (searchTerm.length) {
        try {
          const data = await fetchSearchResults({
            videoHashId,
            shotHashId: videoShotId,
            qTerm: searchTerm,
            type: selectedOption.join(',').toLowerCase()
          });
          dispatch(setSecondarySearchKey({ channelHashId: channelId, searchTerm: searchTerm }));
          dispatch(
            addData({
              videoHashId: keyId,
              videoShotId: videoShotId,
              key: 'search',
              data: data
            })
          );
        } catch (error) {
          console.error('Error fetching search results:', error);
        }
      } else {
        // clear search results and search text
        setSearchTerm('');
        dispatch(
          addData({
            videoHashId: keyId,
            videoShotId: videoShotId,
            key: 'search',
            data: null
          })
        );
      }
    }, 300),
    [searchTerm, videoHashId, videoShotId, selectedOption] // dependencies
  );

  useEffect(() => {
    const video = getSecondaryOpenVideo(secondaryViewVideos, channelId);

    if (searchData && !isDirty && video?.searchKey) {
      setSearchTerm(video?.searchKey);
    }
    return () => {
      setSearchTerm('');
      setIsDirty(false);
    };
  }, [keyId, selected, videoHashId]);

  useEffect(() => {
    if (isDirty) {
      debouncedSearch();
    }
    // Cleanup debounce on component unmount
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch, isDirty]);

  const [people, setPeople] = useState<any[]>([]);
  const speakerSlice = useSelector((state: RootState) => state.speakers);

  useEffect(() => {
    // set speaker and people
    const speakers = getSpeaker(speakerSlice, videoShotId);
    const fetchPeople = async () => {
      const response = await api.get(`/api/videos/${videoHashId}/shots/${videoShotId}/speakers/`);
      setPeople(response.data);
      dispatch(addSpeakers({ shotId: videoShotId, speakerData: response.data }));
    };

    if (videoShotId && !speakers) {
      fetchPeople();
    } else {
      setPeople(speakers);
    }
  }, [videoShotId, channelId]);

  return (
    <div className={`h-full w-full flex flex-col flex-1 overflow-y-auto pb-2`}>
      <div className="px-3">
        <SearchBar
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          selectedOptions={selectedOption}
          setSelectedOptions={setSelectedOption}
          options={options}
          setIsDirty={setIsDirty}
        ></SearchBar>
      </div>
      <div className="flex w-full border-l border-line border-t"></div>
      <div className={`h-full w-full flex flex-col flex-1 overflow-y-auto pb-2`}>
        <SearchResults
          results={searchData}
          searchText={searchTerm}
          videoHashId={videoHashId}
          videoShotId={videoShotId}
          channelHashId={channelId}
          aesKeyId={aesKeyId}
          decryptionKeys={decryptionKeys}
          people={people}
          navigateVideoPage={navigateVideoPage}
          isResizing={isResizing}
        ></SearchResults>
      </div>
    </div>
  );
}
export default memo(SearchView);
